import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipAriaOrder, TooltipMode } from '@magicschool/spellbook/components/Tooltip/Tooltip';
import { useStripRadixDescribedBy } from '@magicschool/spellbook/components/Tooltip/useStripRadixDescribedBy';
import * as TooltipPrimitive from 'radix-ui/tooltip';
import type { ComponentProps } from 'react';

type TooltipTriggerSlotAriaProps = {
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

const mergeAriaIds = (tooltipId: string, existingValue: string | undefined, order: 'first' | 'last'): string => {
  if (!existingValue) return tooltipId;
  return order === 'first' ? `${existingValue} ${tooltipId}` : `${tooltipId} ${existingValue}`;
};

export const TooltipTriggerSlot = Slot<
  Omit<ComponentProps<typeof TooltipPrimitive.Trigger>, 'asChild'> & {
    tooltipId: string;
    /** @internal Injected by Tooltip; equals the `id` it gives Radix Content. */
    contentId: string;
    asToggleTip?: boolean;
    mode?: TooltipMode;
    ariaOrder?: TooltipAriaOrder;
  }
>({
  displayName: 'Tooltip.Trigger',
  preventOverride: true,
  renderDefault: ({
    children,
    ref,
    tooltipId,
    contentId,
    mode = 'description',
    ariaOrder = 'first',
    'aria-describedby': ariaDescribedBy,
    'aria-labelledby': ariaLabelledBy,
    ...props
  }) => {
    // The tooltip names the trigger in label mode and describes it in description mode; none adds nothing.
    const ariaProps: TooltipTriggerSlotAriaProps = {
      'aria-labelledby': mode === 'label' ? mergeAriaIds(tooltipId, ariaLabelledBy, ariaOrder) : ariaLabelledBy,
      'aria-describedby': mode === 'description' ? mergeAriaIds(tooltipId, ariaDescribedBy, ariaOrder) : ariaDescribedBy,
    };

    const triggerRef = useStripRadixDescribedBy(ref, contentId, ariaProps['aria-describedby']);

    return (
      // `asChild` is set to true in all cases to avoid implementation conflicts
      <TooltipPrimitive.Trigger asChild data-spellbook-name="tooltip-trigger" ref={triggerRef} {...props} {...ariaProps}>
        {typeof children === 'string' && children.length > 0 ? (
          <button type="button" className="focus-visible-ring">
            {children}
          </button>
        ) : (
          children
        )}
      </TooltipPrimitive.Trigger>
    );
  },
});

export const TooltipContentSlot = Slot<TextProps>({
  displayName: 'Tooltip.Content',
  preventOverride: true,
  renderDefault: (props) => <Text variant="caption-1" {...props} />,
});

/**
 * @TODO [SPE-47] Rename `Header` Slots to `Heading`
 * https://linear.app/magicschool/issue/SPE-47/rename-header-slots-to-heading
 */
export const TooltipHeaderSlot = Slot<TextProps>({
  displayName: 'Tooltip.Header',
  preventOverride: true,
  renderDefault: (props) => <Text data-spellbook-name="tooltip-header" variant="heading-5" {...props} />,
});
